import React, { useMemo } from 'react'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import {
    Dropdown,
    DropdownButton,
    DropdownContent,
    DropdownHeadSearch,
    DropdownItem,
    DropdownSub,
    DropdownSubContent,
    DropdownSubTrigger,
} from 'ui/components/Dropdown'
import { Tag } from 'ui/components/Tag'
import { Body } from 'ui/components/Text'

import {
    LayoutEditorMultiImageRef,
    useLayoutEditorMultiImageControlState,
} from './hooks/useLayoutEditorMultiImageControlState'
import { LayoutEditorImageControl } from './LayoutEditorImageControl'

type LayoutEditorMultiImageControlProps = {
    value: LayoutEditorMultiImageRef[]
    onChange: (value: LayoutEditorMultiImageRef[]) => void
    fields: FieldDto[]
    maxSelectedFields?: number
    maxSelectedImages?: number
    // If true, only one type of value can be selected at a time.
    restrictSingleType?: boolean
}

export const LayoutEditorMultiImageControl: React.FC<LayoutEditorMultiImageControlProps> = ({
    value,
    onChange,
    fields,
    maxSelectedFields,
    maxSelectedImages,
    restrictSingleType = true,
}) => {
    const {
        fieldOptions,
        isFieldSelected,
        onAddField,
        onRemoveField,
        selectedFieldOptions,
        selectedImages,
        onRemoveImage,
        onUploadClick,
        showValueDropdown,
        showIndividualImageUpload,
        showIndividualFieldUpdate,
        allowFieldUpdate,
        allowImageUpload,
    } = useLayoutEditorMultiImageControlState({
        value,
        onChange,
        fields,
        maxSelectedFields,
        maxSelectedImages,
        restrictSingleType,
    })

    const fieldDropdownItems = useMemo(() => {
        if (fieldOptions.length < 1) {
            return (
                <DropdownItem
                    label={
                        <Body size="m" color="textHelper">
                            No supported fields found
                        </Body>
                    }
                    disabled
                />
            )
        }

        return fieldOptions.map((option) => (
            <DropdownItem
                key={option.value}
                label={option.label}
                startIcon={option.icon}
                onCheckedChange={(checked) => {
                    if (checked) {
                        onAddField(option.value)
                    } else {
                        onRemoveField(option.value)
                    }
                }}
                multiSelect
                checked={isFieldSelected(option.value)}
            />
        ))
    }, [fieldOptions, isFieldSelected, onAddField, onRemoveField])

    return (
        <Box flex column gap="l">
            {selectedImages.length > 0 && (
                <Box flex column gap="l">
                    {selectedImages.map((image) => (
                        <LayoutEditorImageControl
                            key={image.url}
                            value={image.url}
                            onChange={() => onRemoveImage(image.url)}
                        />
                    ))}
                </Box>
            )}
            {selectedFieldOptions.length > 0 && (
                <Box flex wrap gap="s">
                    {selectedFieldOptions.map((field) => (
                        <Tag
                            key={field.value}
                            size="m"
                            shade="light"
                            onRemove={() => onRemoveField(field.value)}
                            showRemoveButton
                            startIcon={field.icon}
                            color="Theme"
                            maxWidth="full"
                            minWidth={0}
                        >
                            {field.label}
                        </Tag>
                    ))}
                </Box>
            )}
            {(showValueDropdown || showIndividualImageUpload || showIndividualFieldUpdate) && (
                <Box>
                    {showIndividualImageUpload && (
                        <Button
                            size="m"
                            variant="dotted"
                            startIcon={{ name: 'Plus' }}
                            onClick={onUploadClick}
                        >
                            Upload image
                        </Button>
                    )}
                    {showIndividualFieldUpdate && (
                        <Dropdown>
                            <DropdownButton variant="dotted" startIcon={{ name: 'Sheet' }}>
                                Select field
                            </DropdownButton>
                            <DropdownContent
                                style={{ width: '290px' }}
                                head={<DropdownHeadSearch />}
                            >
                                {fieldDropdownItems}
                            </DropdownContent>
                        </Dropdown>
                    )}
                    {showValueDropdown && (
                        <Dropdown>
                            <DropdownButton variant="dotted" startIcon={{ name: 'Plus' }}>
                                Upload image
                            </DropdownButton>
                            <DropdownContent style={{ width: '290px' }}>
                                {allowFieldUpdate && (
                                    <DropdownSub>
                                        <DropdownSubTrigger
                                            label="Select field"
                                            startIcon={{ name: 'Sheet' }}
                                        />
                                        <DropdownSubContent head={<DropdownHeadSearch />}>
                                            {fieldDropdownItems}
                                        </DropdownSubContent>
                                    </DropdownSub>
                                )}
                                {allowImageUpload && (
                                    <DropdownItem
                                        label="Upload image"
                                        startIcon={{ name: 'Upload' }}
                                        onClick={onUploadClick}
                                    />
                                )}
                            </DropdownContent>
                        </Dropdown>
                    )}
                </Box>
            )}
        </Box>
    )
}
